import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2, Heart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Common";

const TIER_LABEL = { story: "Keepsake Story", story_premium: "Keepsake Story Premium" };

export default function GiftRedeem() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gift, setGift] = useState(null);
  const [err, setErr] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => { api.get(`/stories/gifts/${code}`).then((r) => setGift(r.data)).catch(() => setErr(true)); }, [code]);

  const redeem = async () => {
    if (!user) { navigate(`/login?next=/gift/${code}`); return; }
    setRedeeming(true);
    try {
      await api.post(`/stories/gifts/${code}/redeem`);
      toast.success("Gift redeemed — let's begin your story");
      navigate("/stories/new");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not redeem"); setRedeeming(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] px-4 text-center"><p className="text-[hsl(var(--muted-foreground))]">This gift link is not valid.</p></div>;
  if (!gift) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]"><Loader2 className="animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="gift-redeem">
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]"><div className="max-w-lg mx-auto px-4 h-16 flex items-center"><Logo /></div></div>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12 text-center">
        <div className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-[hsl(var(--letterbox))] text-[hsl(var(--letterbox-foreground))]"><Gift size={30} /></div>
        <p className="mt-5 font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">A gift for {gift.recipient_name || "you"}</p>
        <h1 className="mt-2 font-display text-3xl font-semibold">Your family wants to hear your story</h1>
        <p className="mt-3 text-[hsl(var(--muted-foreground))]">You've been gifted a <strong>{TIER_LABEL[gift.tier] || "Keepsake Story"}</strong> — a beautiful documentary of your life, guided by a gentle AI interviewer.</p>
        {gift.message && <div className="mt-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-4 italic text-[hsl(var(--foreground))]">"{gift.message}"</div>}
        {gift.redeemed ? (
          <p className="mt-6 text-sm text-[hsl(var(--muted-foreground))]">This gift has already been redeemed.</p>
        ) : (
          <button data-testid="gift-redeem-button" onClick={redeem} disabled={redeeming}
            className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-base hover:opacity-90 disabled:opacity-60">
            {redeeming ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} />} Begin my story
          </button>
        )}
      </div>
    </div>
  );
}
